import { datadogLogs } from '@datadog/browser-logs'
import { QuestionKind, RenderedQuestionnaire } from 'model/questionnaire'
import { useEffect, useState } from 'react'
import { RESPONDENT_QUESTIONNAIRE_API_PATH } from 'utils/constants'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { shouldUseQuestionnaireEndpoint } from 'utils/featureFlags'
import { legacyApiUrlEndpoint } from 'utils/helper'
import { useCintQueryParams } from './useCintQueryParams'
import useQueryParams from './useQueryParams'
import { axiosWithRetry } from './usePostCallback'
import {
  QuestionnaireParams,
  useOptionalQuestionnaireParams
} from './useQuestionnaireParams'

interface Data {
  renderedQuestionnaire: RenderedQuestionnaire | undefined
  loading: boolean
  error: boolean
}

const ARLO_AUTHOR = 'arlo'

// Read from the URL, not QuestionnaireParams — otherwise useMetadataQueryParams
// (value-based exclusion) would drop source/type from live respondent metadata.
export interface PreviewSkipParams {
  preview: boolean
  source: string | null
  type: string | null
}

// A qual preview can still carry a leftover quant questionnaire (quant->qual is
// non-destructive). Drop the Arlo-authored questionnaire entries, keeping screeners
// and hand-added questions (never tagged `arlo`). Live delete is PSET-988's.
export const skipArloAuthoredQuestionnaireForPreview = (
  renderedQuestionnaire: RenderedQuestionnaire,
  { preview, source, type }: PreviewSkipParams
): RenderedQuestionnaire => {
  // Also gate on redirectToFDChat: a stale type=fdchat link on a since-quant survey
  // would otherwise strip the questionnaire with no interview to fall through to.
  const isOrchestraFdChatPreview =
    preview &&
    source === 'orchestra' &&
    type === 'fdchat' &&
    renderedQuestionnaire.redirectToFDChat

  if (!isOrchestraFdChatPreview) {
    return renderedQuestionnaire
  }

  // Renumber renderedEntryPosition contiguously — the flow uses it as the array
  // index. entryNumber is left as-is (routing/looping key off it).
  const entries = renderedQuestionnaire.questionnaire.entries
    .filter(
      (entry) =>
        !(
          entry.questionKind === QuestionKind.QuestionnaireKind &&
          entry.metadata?.author === ARLO_AUTHOR
        )
    )
    .map((entry, index) => ({ ...entry, renderedEntryPosition: index }))

  return {
    ...renderedQuestionnaire,
    questionnaire: { ...renderedQuestionnaire.questionnaire, entries }
  }
}

const useRenderedQuestionnaire: (
  language: string | undefined,
  skip?: boolean
) => Data = (language, skip = false) => {
  const questionnaireParams = useOptionalQuestionnaireParams()
  const cintQueryParams = useCintQueryParams()
  const queryParams = useQueryParams()
  const previewSource = queryParams.get('source')
  const previewType = queryParams.get('type')
  // Identity of the fetch the current inputs call for; null when none is due
  // (deferred, or no params). `loading` derives from it synchronously below,
  // so there is never a committed frame where a fetch is due but the hook
  // still reports settled — route guards would treat such a frame as final
  // and, e.g., mount /complete's submission sequence twice around the fetch.
  const requestKey = skip || !questionnaireParams ? null : language ?? ''
  const [settled, setSettled] = useState<{
    key: string | null
    renderedQuestionnaire: RenderedQuestionnaire | undefined
    error: boolean
  }>({ key: null, renderedQuestionnaire: undefined, error: false })

  useEffect(() => {
    // Guard against out-of-order responses. When `language` (or `skip`)
    // changes, the effect re-runs and fires a new request before the previous
    // one settles. Without this flag a stale no-language response (the English
    // master returned by the legacy endpoint) could resolve last and clobber
    // the translated questionnaire, so the respondent would see English.
    let ignore = false

    if (requestKey === null || !questionnaireParams) {
      // Deferred fetch — prior data (if any) stays available while skipped.
      return () => {
        ignore = true
      }
    }

    const fetchRenderedQuestionnaire = async (
      mandatoryQueryParams: QuestionnaireParams
    ) => {
      const { surveyId, respondentId, panelSupplier, preview } =
        mandatoryQueryParams

      // The legacy questionnaire endpoint has no notion of language. Once we
      // know the respondent's chosen language we must hit the new endpoint or
      // their translated questionnaire is silently swapped for the master.
      const useNewEndpoint =
        !!language || shouldUseQuestionnaireEndpoint(respondentId)
      const apiUsed = useNewEndpoint
        ? API_DISTRIBUTION_FACET.NEW
        : API_DISTRIBUTION_FACET.LEGACY

      const languageParam = language ? `&language=${language}` : ''
      const url = useNewEndpoint
        ? coreRespondentUrl(
            `/surveys/${surveyId}/questionnaire?supplier_respondent_id=${respondentId}&is_preview=${preview}&panel_supplier_code=${panelSupplier}${languageParam}`
          )
        : `${legacyApiUrlEndpoint}${RESPONDENT_QUESTIONNAIRE_API_PATH}?surveyId=${surveyId}&panelSupplierCode=${panelSupplier}&respondentId=${respondentId}&preview=${preview}`

      datadogLogs.logger.info('Fetching rendered questionnaire', {
        apiUsed,
        respondentId
      })

      try {
        const { data }: { data: RenderedQuestionnaire } = await axiosWithRetry({
          method: 'get',
          url,
          timeout: 30000
        })
        if (ignore) {
          return
        }
        datadogLogs.logger.info('Received questionnaire', {
          prequalified: false,
          apiUsed,
          respondentId
        })
        setSettled({
          key: requestKey,
          renderedQuestionnaire: data
            ? skipArloAuthoredQuestionnaireForPreview(data, {
                preview: mandatoryQueryParams.preview,
                source: previewSource,
                type: previewType
              })
            : data,
          error: false
        })
      } catch (error) {
        if (ignore) {
          return
        }
        datadogLogs.logger.error(
          'Network Failure: Could not fetch rendered questionnaire',
          {
            error,
            mandatoryQueryParams,
            apiUsed,
            respondentId
          }
        )
        setSettled({
          key: requestKey,
          renderedQuestionnaire: undefined,
          error: true
        })
      }
    }

    fetchRenderedQuestionnaire(questionnaireParams)

    return () => {
      ignore = true
    }
  }, [
    cintQueryParams,
    questionnaireParams,
    language,
    requestKey,
    previewSource,
    previewType
  ])

  // A due fetch counts as loading until a response (or error) settles for the
  // current inputs — even with an empty/absent body, settling ends loading.
  // Prior data stays available while a new language loads.
  return {
    renderedQuestionnaire: settled.renderedQuestionnaire,
    loading: requestKey !== null && settled.key !== requestKey,
    error: settled.error
  }
}

export default useRenderedQuestionnaire
